import { clsx } from 'clsx'
import { lazy, Suspense, useRef, useState } from 'react'
import { useOnClickOutside } from 'usehooks-ts'

import AsyncButton from ':/_console/js/components/AsyncButton.tsx'
import announcementDefaultIcon from '{cImg}/announcement-default.svg'
import bellIcon from '{cImg}/bell.svg'
import Tooltip from '~/components/Tooltip/index.tsx'
import type { Announcement } from '~/features/announcements/data.ts'
import {
  useAnnouncements,
  useClearAnnouncementsBell,
  useMarkAllAnnouncementsAsRead,
  useMarkOneAnnouncementAsRead,
} from '~/features/announcements/data.ts'

import MarkAllAsReadTooltip from './MarkAllAsReadButton.tsx'

const AllAnnouncementsModal = lazy(() => import('./AllAnnouncementsModal.tsx'))
const AnnouncementModal = lazy(() => import('./AnnouncementModal.tsx'))

function HeaderAnnouncementsTooltip() {
  const { data, error } = useAnnouncements()
  const clearBell = useClearAnnouncementsBell()

  const [isDropdownOpen, setIsDropdownOpen] = useState(false)

  // Close the dropdown after clicking outside of it
  const root = useRef<HTMLDivElement>(null)
  // @ts-expect-error usehooks-ts types are not compatible with React 19 yet
  useOnClickOutside(root, (event) => {
    if (event.target) {
      const target = event.target as HTMLElement
      const clickingOnModal = target.closest('.modal-overlay')
      if (!clickingOnModal) {
        setIsDropdownOpen(false)
      }
    }
  })

  return (
    <div ref={root}>
      {!data && !error && <Placeholder />}

      {data && (
        <Tooltip
          type="dropdown"
          placement="bottom-end"
          content={
            <DropdownContent
              announcements={data.announcements}
              closeDropdown={() => setIsDropdownOpen(false)}
            />
          }
          visible={isDropdownOpen}
        >
          <button
            type="button"
            className={clsx('btn', 'btn--clear', 'header__nav-link', 'header-announcements', {
              'header-announcements--has-new': data.has_active_alarm_bell,
            })}
            onClick={() => {
              setIsDropdownOpen((open) => !open)
              if (!isDropdownOpen) {
                clearBell.execute()
              }
            }}
          >
            <img className="header-announcements__img" src={bellIcon} alt="Notifications" />
          </button>
        </Tooltip>
      )}
    </div>
  )
}

function Placeholder() {
  return (
    <button
      type="button"
      className={clsx('btn', 'btn--clear', 'header__nav-link', 'header-announcements')}
    >
      <img className="header-announcements__img" src={bellIcon} alt="Notifications" />
    </button>
  )
}

interface DropdownContentProps {
  announcements: Announcement[]
  closeDropdown: () => void
}

function DropdownContent(props: DropdownContentProps) {
  const { announcements = [], closeDropdown } = props

  const markOneAsRead = useMarkOneAnnouncementAsRead()
  const markAllAsRead = useMarkAllAnnouncementsAsRead()

  const firstFiveAnnouncements = announcements.slice(0, 5)

  return (
    <div className="bell-announcements">
      <div className="bell-announcements__header">
        <h3 className="bell-announcements__title">Notifications</h3>
        <MarkAllAsReadTooltip />
      </div>

      <ul className="bell-announcements__list">
        {firstFiveAnnouncements.length === 0 ? (
          <div className="bell-announcements__no-announcements-placeholder">
            No notifications yet
          </div>
        ) : (
          firstFiveAnnouncements.map((announcement) => (
            <AnnouncementListItem
              key={announcement.id}
              announcement={announcement}
              onModalOpen={() => {
                closeDropdown()
                markOneAsRead.execute(announcement)
              }}
            />
          ))
        )}
      </ul>

      <Suspense
        fallback={
          <AsyncButton status="loading" className="btn bell-announcements__show-more">
            See all activity
          </AsyncButton>
        }
      >
        <AllAnnouncementsModal
          announcements={announcements}
          onModalOpen={() => {
            closeDropdown()
            markAllAsRead.execute()
          }}
        />
      </Suspense>
    </div>
  )
}

interface AnnouncementListItemProps {
  announcement: Announcement
  onModalOpen: () => void
}

function AnnouncementListItem(props: AnnouncementListItemProps) {
  const { announcement, onModalOpen } = props

  return (
    <li
      key={announcement.id}
      className={clsx('bell-announcements__item', {
        'bell-announcements__item--unread': !announcement.consumed,
      })}
    >
      <Suspense
        fallback={
          <button type="button" className="btn--unstyled bell-announcements__link">
            <img
              className="bell-announcements__icon"
              src={announcementDefaultIcon}
              alt="Announcement"
            />
            <div className="bell-announcements__content">
              <div className="bell-announcements__text">…</div>
              <div className="bell-announcements__date">…</div>
            </div>
          </button>
        }
      >
        <AnnouncementModal onModalOpen={onModalOpen} announcement={announcement} />
      </Suspense>
    </li>
  )
}

export default HeaderAnnouncementsTooltip
